import { OwcMixin } from '@owc-types/ComponentInitializeDispatcher';
import { getStore } from '@/store';
import { Subscription } from '@seamless/store';
import { BaseDataJson } from '../../_types/BaseDataJSON';
import {
  OWCPersonalisationData,
  OWCPersonalisationState,
  OWC_PERSONALISATION_CONNECTION,
} from '../SeamlessStore/connections';

export type ElementPersonalisationMethods<T> = {
  getRootNode(options?: GetRootNodeOptions): OwcComponentRoot<T>;
};

export type OwcComponentWithTypedRoot<T> = {
  $el?: ElementPersonalisationMethods<T>;
};

export type OwcComponentRoot<T> = Node & {
  host?: Element & T;
};

export type PersonalisationHost<T = any> = {
  personalisation: (payload: T) => void;
};

export type PersonalisationData<T> = {
  personalisationPayload?: T;
  componentData?: T;
  subscription?: Subscription;
};

export type PersonalisationComputed<T> = {
  personaliseableData: T & BaseDataJson;
};

// This type can be added to type a component and should be fairly flat mapping
export type PersonalizationAddon<T> = PersonalisationData<T> & PersonalisationComputed<T>;
// Thsi type can be used to type the mixin, providing validations
export type PersonalizationMixin<T = any> = OwcMixin<
  OwcComponentWithTypedRoot<PersonalisationHost>,
  PersonalisationData<T>,
  {},
  PersonalisationComputed<T>,
  {}
>;

const mixin: PersonalizationMixin = {
  data() {
    return {
      personalisationPayload: undefined,
    };
  },
  computed: {
    personaliseableData() {
      return this.personalisationPayload ?? this.componentData;
    },
  },
  async mounted() {
    const store = await getStore();

    const componentId = this.$el?.getRootNode().host?.getAttribute('component-id');

    this.subscription = store.subscribe(OWC_PERSONALISATION_CONNECTION, (state: OWCPersonalisationState) => {
      const personalisation = state.filter((el: OWCPersonalisationData) => el.componentId === componentId);

      if (personalisation.length > 0) {
        this.personalisationPayload = personalisation[0].personalisationData;
      }
    });

    this.$nextTick(() => {
      const host = this.$el?.getRootNode().host;
      if (host) {
        host.personalisation = payload => {
          this.personalisationPayload = payload;
        };
        host.dispatchEvent(
          new CustomEvent('personalisation-initialize', {
            bubbles: true,
            composed: true,
            detail: {
              element: host,
            },
          }),
        );
      }
    });
  },
  destroyed() {
    this.subscription?.unsubscribe();
  },
};

export default mixin;
